import { ArrowClockwiseIcon, HouseIcon, WarningIcon, WifiSlashIcon } from '@phosphor-icons/react';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Button } from './button';

import { cn } from '@/lib/utils';

export type ErrorCategory = 'network' | 'permission' | 'validation' | 'runtime' | 'unknown';
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo, context?: ErrorContext) => void;
  resetKeys?: Array<unknown>;
  level?: 'app' | 'page' | 'section' | 'component';
  variant?: 'fullscreen' | 'card' | 'inline' | 'minimal';
  showErrorId?: boolean;
  showNavigation?: boolean;
  showDetails?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  isolate?: boolean;
  customActions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'neutral' | 'outline' | 'ghost';
  }>;
  className?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
  retryCount: number;
  category: ErrorCategory;
  severity: ErrorSeverity;
}

interface ErrorContext {
  category: ErrorCategory;
  severity: ErrorSeverity;
  retryCount: number;
  errorId: string;
}

const categorizeError = (error: Error): { category: ErrorCategory; severity: ErrorSeverity } => {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  if (name.includes('network') || message.includes('fetch') || message.includes('network')) {
    return { category: 'network', severity: 'high' };
  }

  if (
    message.includes('permission') ||
    message.includes('unauthorized') ||
    message.includes('forbidden')
  ) {
    return { category: 'permission', severity: 'high' };
  }

  if (name.includes('validation') || name.includes('type') || message.includes('invalid')) {
    return { category: 'validation', severity: 'medium' };
  }

  if (name.includes('reference') || name.includes('syntax')) {
    return { category: 'runtime', severity: 'critical' };
  }

  return { category: 'unknown', severity: 'medium' };
};

/**
 * A lazy page whose file could not be fetched. Nearly always a tab left open across a deploy, whose old file no
 * longer exists. The connection is fine, so saying "check your internet" would be untrue, and a retry cannot
 * work because the browser remembers the failed import. Only a reload helps.
 */
const isPageLoadError = (error: Error | null | undefined): boolean =>
  /dynamically imported module|importing a module script failed|error loading dynamically|chunkloaderror/i.test(
    `${error?.name ?? ''} ${error?.message ?? ''}`,
  );

const PAGE_LOAD_MESSAGE =
  'This page did not finish loading. Mora may have just updated. Reload to continue.';

const getErrorMessage = (category: ErrorCategory): string => {
  switch (category) {
    case 'network':
      return 'Unable to connect. Please check your internet connection.';
    case 'permission':
      return "You don't have permission to access this resource.";
    case 'validation':
      return 'The data provided is invalid. Please check and try again.';
    case 'runtime':
      return 'A critical error occurred. Our team has been notified.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

export class UnifiedErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;
  private retryTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
      retryCount: 0,
      category: 'unknown',
      severity: 'medium',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    const { category, severity } = categorizeError(error);

    return {
      hasError: true,
      error,
      errorId,
      category,
      severity,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { category, severity } = categorizeError(error);

    this.setState({
      error,
      errorInfo,
      category,
      severity,
    });

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.group(`🚨 ${this.props.level || 'Component'} Error Boundary`);
      console.error('🚨 ACTUAL ERROR OBJECT:', error);
      console.error('🚨 ACTUAL ERROR MESSAGE:', error.message);
      console.error('Category:', category);
      console.error('Severity:', severity);
      console.error('Component Stack:', errorInfo.componentStack);
      // eslint-disable-next-line no-console
      console.groupEnd();
    } else {
      console.error(`Error ${this.state.errorId} [${category}/${severity}]:`, error.message);
    }

    const context: ErrorContext = {
      category,
      severity,
      retryCount: this.state.retryCount,
      errorId: this.state.errorId,
    };

    if (import.meta.env.PROD) {
      import('@sentry/react')
        .then((Sentry) => {
          Sentry.captureException(error, {
            contexts: {
              react: {
                componentStack: errorInfo.componentStack,
              },
            },
          });
        })
        .catch(() => {});
    }

    this.props.onError?.(error, errorInfo, context);

    if (this.shouldAutoRetry()) {
      this.scheduleRetry();
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetKeys?.some((key, index) => key !== prevProps.resetKeys?.[index])) {
        this.resetErrorBoundary();
      }
    }
  }

  componentWillUnmount(): void {
    if (this.resetTimeoutId) clearTimeout(this.resetTimeoutId);
    if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
  }

  private shouldAutoRetry = (): boolean => {
    const { maxRetries = 0 } = this.props;
    const { retryCount, category } = this.state;

    return category === 'network' && retryCount < maxRetries;
  };

  private scheduleRetry = (): void => {
    const { retryDelay = 1000 } = this.props;
    const { retryCount } = this.state;

    const delay = Math.min(retryDelay * Math.pow(2, retryCount), 30000);

    this.retryTimeoutId = window.setTimeout(() => {
      this.retry();
    }, delay);
  };

  private retry = (): void => {
    this.setState((prevState) => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1,
    }));
  };

  resetErrorBoundary = (): void => {
    if (this.resetTimeoutId) clearTimeout(this.resetTimeoutId);
    if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);

    this.resetTimeoutId = window.setTimeout(() => {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        errorId: '',
        retryCount: 0,
        category: 'unknown',
        severity: 'medium',
      });
    }, 0);
  };

  reloadPage = (): void => {
    window.location.reload();
  };

  goHome = (): void => {
    window.location.href = '/';
  };

  goBack = (): void => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.goHome();
    }
  };

  private renderFullscreen = (): ReactNode => {
    const { error, errorInfo, category } = this.state;
    const { showDetails = import.meta.env.DEV } = this.props;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm mx-auto text-center">
          <div className="mb-6">
            {category === 'network' ? (
              <WifiSlashIcon className="w-8 h-8 mx-auto text-muted-foreground/60" />
            ) : (
              <WarningIcon className="w-8 h-8 mx-auto text-muted-foreground/60" />
            )}
          </div>

          <h1 className="text-lg font-semibold text-foreground mb-2">An error occurred</h1>
          <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
            {isPageLoadError(error)
              ? PAGE_LOAD_MESSAGE
              : category === 'network'
                ? 'Unable to connect. Check your internet and try again.'
                : "We're looking into it. Try heading back to the home page."}
          </p>

          <Button
            onClick={isPageLoadError(error) ? this.reloadPage : this.goHome}
            variant="neutral"
          >
            {isPageLoadError(error) ? (
              <ArrowClockwiseIcon className="w-4 h-4 mr-2" />
            ) : (
              <HouseIcon className="w-4 h-4 mr-2" />
            )}
            {isPageLoadError(error) ? 'Reload' : 'Go Home'}
          </Button>

          {showDetails && error && (
            <details className="mt-10 text-left rounded-lg bg-muted/40 border border-border/60 p-3">
              <summary className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
                Developer details
              </summary>
              <div className="mt-2 space-y-2">
                <p className="text-xs font-mono text-muted-foreground break-all">{error.message}</p>
                {errorInfo && (
                  <pre className="text-xs text-muted-foreground/70 overflow-auto max-h-32 whitespace-pre-wrap">
                    {errorInfo.componentStack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  };

  private renderCard = (): ReactNode => {
    const { error, category } = this.state;
    const { showDetails = import.meta.env.DEV } = this.props;

    return (
      <div className="w-full max-w-sm mx-auto my-8 text-center">
        <div className="mb-4">
          {category === 'network' ? (
            <WifiSlashIcon className="h-6 w-6 mx-auto text-muted-foreground/60" />
          ) : (
            <WarningIcon className="h-6 w-6 mx-auto text-muted-foreground/60" />
          )}
        </div>

        <h2 className="text-base font-semibold text-foreground mb-1">An error occurred</h2>
        <p className="text-sm text-muted-foreground mb-6">
          {isPageLoadError(error)
            ? PAGE_LOAD_MESSAGE
            : category === 'network'
              ? 'Unable to connect. Check your internet and try again.'
              : "We're looking into it. Try again or head back home."}
        </p>

        <div className="flex justify-center gap-2">
          <Button
            variant="neutral"
            size="sm"
            onClick={isPageLoadError(error) ? this.reloadPage : this.resetErrorBoundary}
          >
            <ArrowClockwiseIcon className="mr-1.5 h-3.5 w-3.5" />
            {isPageLoadError(error) ? 'Reload' : 'Try again'}
          </Button>
        </div>

        {showDetails && error && (
          <details className="mt-6 text-left rounded-lg bg-muted/40 border border-border/60 p-3">
            <summary className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
              Developer details
            </summary>
            <p className="mt-2 text-xs font-mono text-muted-foreground break-all">
              {error.message}
            </p>
          </details>
        )}
      </div>
    );
  };

  private renderInline = (): ReactNode => {
    const { category } = this.state;

    return (
      <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          {category === 'network' ? (
            <WifiSlashIcon className="h-4 w-4 text-muted-foreground/60 mt-0.5 shrink-0" />
          ) : (
            <WarningIcon className="h-4 w-4 text-muted-foreground/60 mt-0.5 shrink-0" />
          )}
          <div className="flex-1 space-y-2">
            <p className="text-sm text-muted-foreground">{getErrorMessage(category)}</p>
            <Button variant="outline" size="sm" onClick={this.resetErrorBoundary}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  };

  private renderMinimal = (): ReactNode => {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">An error occurred</p>
        <Button variant="outline" onClick={this.resetErrorBoundary}>
          Try again
        </Button>
      </div>
    );
  };

  render(): ReactNode {
    const { hasError } = this.state;
    const {
      children,
      fallback,
      variant = 'card',
      level = 'component',
      isolate = false,
      className,
    } = this.props;

    if (!hasError) {
      if (isolate) {
        return <div className={cn('contents', className)}>{children}</div>;
      }
      return children;
    }

    if (fallback) {
      return fallback;
    }

    const useFullscreen = variant === 'fullscreen' || level === 'app';

    if (useFullscreen) return this.renderFullscreen();
    if (variant === 'inline') return this.renderInline();
    if (variant === 'minimal') return this.renderMinimal();
    return this.renderCard();
  }
}
